export interface BluetoothScannerDevice {
  id: string;
  name: string;
  connected: boolean;
  batteryLevel?: number;
  lastSeen: string;
  capabilities: string[];
}

export interface ScannerConfig {
  autoConnect: boolean;
  scanTimeout: number;
  retryAttempts: number;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  scanPrefix?: string;
  scanSuffix?: string;
}

export class BluetoothScannerService {
  private device: BluetoothDevice | null = null;
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private config: ScannerConfig;
  private onScanCallback?: (barcode: string) => void;
  private onConnectionCallback?: (connected: boolean) => void;
  private scanBuffer: string = '';

  constructor(config: Partial<ScannerConfig> = {}) {
    this.config = {
      autoConnect: true,
      scanTimeout: 5000,
      retryAttempts: 3,
      soundEnabled: true,
      vibrationEnabled: true,
      ...config
    };
  }

  async isBluetoothSupported(): Promise<boolean> {
    return 'bluetooth' in navigator && 'requestDevice' in navigator.bluetooth;
  }

  async requestDevice(): Promise<BluetoothScannerDevice | null> {
    try {
      if (!await this.isBluetoothSupported()) {
        throw new Error('Bluetooth not supported in this browser');
      }

      const device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: ['human_interface_device'] },
          { namePrefix: 'Scanner' },
          { namePrefix: 'Zebra' },
          { namePrefix: 'Honeywell' },
          { namePrefix: 'Datalogic' }
        ],
        optionalServices: ['battery_service', 'device_information']
      });

      this.device = device;
      
      // Set up disconnect handler
      device.addEventListener('gattserverdisconnected', () => {
        this.handleDisconnection();
      });

      const scannerDevice: BluetoothScannerDevice = {
        id: device.id,
        name: device.name || 'Unknown Scanner',
        connected: false,
        lastSeen: new Date().toISOString(),
        capabilities: ['barcode_scanning']
      };

      return scannerDevice;
    } catch (error) {
      console.error('Failed to request Bluetooth device:', error);
      return null;
    }
  }

  async connect(): Promise<boolean> {
    try {
      if (!this.device) {
        throw new Error('No device selected');
      }

      const server = await this.device.gatt?.connect();
      if (!server) {
        throw new Error('Failed to connect to GATT server');
      }

      // Try to connect to HID service for scanner input
      try {
        const service = await server.getPrimaryService('human_interface_device');
        const characteristics = await service.getCharacteristics();
        
        // Find the input characteristic
        for (const char of characteristics) {
          if (char.properties.notify) {
            this.characteristic = char;
            await char.startNotifications();
            char.addEventListener('characteristicvaluechanged', (event) => {
              this.handleScanData(event);
            });
            break;
          }
        }
      } catch (error) {
        console.warn('HID service not available, trying alternative connection method');
        await this.connectAlternative(server);
      }

      this.onConnectionCallback?.(true);
      return true;
    } catch (error) {
      console.error('Failed to connect to scanner:', error);
      this.onConnectionCallback?.(false);
      return false;
    }
  }

  async disconnect(): Promise<void> {
    try {
      if (this.characteristic) {
        await this.characteristic.stopNotifications();
        this.characteristic = null;
      }

      if (this.device?.gatt?.connected) {
        this.device.gatt.disconnect();
      }

      this.onConnectionCallback?.(false);
    } catch (error) {
      console.error('Failed to disconnect scanner:', error);
    }
  }

  async getBatteryLevel(): Promise<number | null> {
    try {
      if (!this.device?.gatt?.connected) {
        return null;
      }

      const service = await this.device.gatt.getPrimaryService('battery_service');
      const characteristic = await service.getCharacteristic('battery_level');
      const value = await characteristic.readValue();
      
      return value.getUint8(0);
    } catch (error) {
      console.warn('Could not read battery level:', error);
      return null;
    }
  }

  onScan(callback: (barcode: string) => void): void {
    this.onScanCallback = callback;
  }

  onConnectionChange(callback: (connected: boolean) => void): void {
    this.onConnectionCallback = callback;
  }

  isConnected(): boolean {
    return this.device?.gatt?.connected || false;
  }

  getDeviceInfo(): BluetoothScannerDevice | null {
    if (!this.device) return null;

    return {
      id: this.device.id,
      name: this.device.name || 'Unknown Scanner',
      connected: this.isConnected(),
      lastSeen: new Date().toISOString(),
      capabilities: ['barcode_scanning']
    };
  }

  // Simulate scan for testing
  simulateScan(barcode: string): void {
    this.processScanData(barcode);
  }

  private async connectAlternative(server: BluetoothRemoteGATTServer): Promise<void> {
    // Alternative connection method for scanners that don't use standard HID
    try {
      // Try generic attribute service
      const service = await server.getPrimaryService('generic_attribute');
      const characteristics = await service.getCharacteristics();
      
      for (const char of characteristics) {
        if (char.properties.notify || char.properties.read) {
          this.characteristic = char;
          if (char.properties.notify) {
            await char.startNotifications();
            char.addEventListener('characteristicvaluechanged', (event) => {
              this.handleScanData(event);
            });
          }
          break;
        }
      }
    } catch (error) {
      console.warn('Alternative connection method failed:', error);
      // Set up keyboard input fallback
      this.setupKeyboardFallback();
    }
  }

  private setupKeyboardFallback(): void {
    // Fallback to keyboard input for scanners that act as keyboards
    let scanStartTime = 0;
    let scanData = '';

    const handleKeyPress = (event: KeyboardEvent) => {
      const now = Date.now();
      
      // Reset if too much time has passed (likely manual typing)
      if (now - scanStartTime > 100) {
        scanData = '';
        scanStartTime = now;
      }

      if (event.key === 'Enter') {
        if (scanData.length > 0) {
          this.processScanData(scanData);
          scanData = '';
        }
      } else if (event.key.length === 1) {
        scanData += event.key;
        scanStartTime = now;
      }
    };

    document.addEventListener('keypress', handleKeyPress);
  }

  private handleScanData(event: Event): void {
    const target = event.target as BluetoothRemoteGATTCharacteristic;
    const value = target.value;
    
    if (!value) return;

    // Convert ArrayBuffer to string
    const decoder = new TextDecoder();
    const data = decoder.decode(value);
    
    this.scanBuffer += data;

    // Check for scan termination (usually carriage return or newline)
    if (this.scanBuffer.includes('\r') || this.scanBuffer.includes('\n')) {
      const barcode = this.scanBuffer.replace(/[\r\n]/g, '').trim();
      this.processScanData(barcode);
      this.scanBuffer = '';
    }
  }

  private processScanData(barcode: string): void {
    if (!barcode) return;

    // Apply prefix/suffix filtering
    let processedBarcode = barcode;
    
    if (this.config.scanPrefix && processedBarcode.startsWith(this.config.scanPrefix)) {
      processedBarcode = processedBarcode.substring(this.config.scanPrefix.length);
    }
    
    if (this.config.scanSuffix && processedBarcode.endsWith(this.config.scanSuffix)) {
      processedBarcode = processedBarcode.substring(0, processedBarcode.length - this.config.scanSuffix.length);
    }

    // Provide feedback
    if (this.config.soundEnabled) {
      this.playBeep();
    }
    
    if (this.config.vibrationEnabled && 'vibrate' in navigator) {
      navigator.vibrate(100);
    }

    // Call the scan callback
    this.onScanCallback?.(processedBarcode);
  }

  private handleDisconnection(): void {
    this.characteristic = null;
    this.onConnectionCallback?.(false);
  }

  private playBeep(): void {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'square';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.warn('Could not play beep sound:', error);
    }
  }
}

export const bluetoothScannerService = new BluetoothScannerService();